import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { VALID_LANGUAGE_CODES } from './config/languages';

type LanguageCode = 'en' | 'fr';

interface LanguageContextProps {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

const getLanguageFromURL = (): LanguageCode => {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0];

  if (VALID_LANGUAGE_CODES.includes(firstSegment as LanguageCode)) {
    return firstSegment as LanguageCode;
  }

  // Default to 'fr' if no valid language found in URL
  return 'fr';
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<LanguageCode>(getLanguageFromURL());

  const setLanguage = (lang: LanguageCode) => {
    setLanguageState(lang);
  };

  useEffect(() => {
    // Listen for popstate events (browser back/forward)
    const handlePopState = () => {
      const newLang = getLanguageFromURL();
      setLanguageState(newLang);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextProps => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};